import React, { useCallback, useEffect, useRef } from 'react';
import {
    Checkbox,
    CheckboxGroup,
    FormControl,
    FormErrorMessage,
    FormHelperText,
    FormLabel,
    Input,
    Radio,
    RadioGroup,
    Select,
    Stack,
    Text,
    Textarea
} from '@chakra-ui/react';
import { Controller, Path, useFormContext } from 'react-hook-form';
import { useOutletContext } from 'react-router-dom';
import { choiceSeparator } from 'studio_react/questionnaire/common';
import { useTranslate } from 'ts/common/hooks';
import type { QuestionnaireContext } from 'client_react/questionnaire/QuestionnaireApp';
import type { QuestionnaireFormValues } from 'client_react/questionnaire/components/QuestionnaireQuestionsForm';

const TEXT_SHORT_MAX_LENGTH = 500;
const TEXT_LONG_MAX_LENGTH = 3000;
const AUTO_SAVE_DEBOUNCE_MS = 2000;

function serializeResponseValue(value: unknown): string | null {
    if (value === undefined || value === null) {
        return null;
    }

    if (typeof value === 'string') {
        return value;
    }

    if (Array.isArray(value)) {
        return value.filter((v) => !!v).join(choiceSeparator);
    }

    return String(value);
}

export interface QuestionnaireQuestionFieldProps {
    question: SpApi.Client.IQuestionnaireQuestion;
    pageIndex: number;
}

const QuestionnaireQuestionField: React.FC<QuestionnaireQuestionFieldProps> = ({
    question,
    pageIndex
}) => {
    const t = useTranslate('clientQuestionnaire');

    const {
        register,
        control,
        watch,
        getValues,
        formState: { errors }
    } = useFormContext<QuestionnaireFormValues>();

    const { saveQuestionResponse } = useOutletContext<QuestionnaireContext>();

    const fieldName = `pages.${pageIndex}.${question.id}` as Path<QuestionnaireFormValues>;
    const isSavedRef = useRef(true);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const saveNow = useCallback(() => {
        if (isSavedRef.current) {
            return;
        }

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }

        const current = getValues(fieldName);
        saveQuestionResponse(question.id, serializeResponseValue(current));
        isSavedRef.current = true;
    }, [fieldName, getValues, question.id, saveQuestionResponse]);

    const saveDebounced = useCallback(
        (value: unknown) => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }

            timeoutRef.current = setTimeout(() => {
                saveQuestionResponse(question.id, serializeResponseValue(value));
                isSavedRef.current = true;
                timeoutRef.current = null;
            }, AUTO_SAVE_DEBOUNCE_MS);
        },
        [question.id, saveQuestionResponse]
    );

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
        };
    }, []);

    const fieldError = errors?.pages?.[String(pageIndex)]?.[String(question.id)];

    return (
        <FormControl
            key={question.id}
            isRequired={question.required}
            isInvalid={!!fieldError}
            paddingX={{ base: '16px', lg: '40px' }}
            paddingY="24px"
            boxSizing="border-box"
        >
            {question.responseType === 'choices_radio' ||
            question.responseType === 'choices_checkboxes' ? (
                <Text fontSize="16px" marginBottom="16px">
                    {question.questionText}
                </Text>
            ) : (
                <FormLabel fontSize="16px" marginBottom="16px">
                    {question.questionText}
                </FormLabel>
            )}
            {question.responseType === 'text_short' && (
                <>
                    <Input
                        type="text"
                        placeholder={question.responsePlaceholder ?? undefined}
                        maxLength={TEXT_SHORT_MAX_LENGTH}
                        {...((): ReturnType<typeof register> => {
                            const reg = register(fieldName, {
                                required: question.required ? t('fieldRequired') : false,
                                maxLength: {
                                    value: TEXT_SHORT_MAX_LENGTH,
                                    message: t('maxCharactersExceeded', {
                                        max: TEXT_SHORT_MAX_LENGTH
                                    })
                                }
                            });
                            return {
                                ...reg,
                                onBlur: async (e) => {
                                    await reg.onBlur(e);
                                    saveNow();
                                },
                                onChange: async (e) => {
                                    await reg.onChange(e);
                                    isSavedRef.current = false;
                                    saveDebounced(e.target.value);
                                }
                            };
                        })()}
                    />
                    <FormHelperText fontSize="xs">
                        {t('characterCount', {
                            current: (watch(fieldName) as unknown as string)?.length ?? 0,
                            max: TEXT_SHORT_MAX_LENGTH
                        })}
                    </FormHelperText>
                </>
            )}
            {question.responseType === 'text_long' && (
                <>
                    <Textarea
                        variant="outline"
                        placeholder={question.responsePlaceholder ?? undefined}
                        minHeight="120px"
                        maxLength={TEXT_LONG_MAX_LENGTH}
                        {...((): ReturnType<typeof register> => {
                            const reg = register(fieldName, {
                                required: question.required ? t('fieldRequired') : false,
                                maxLength: {
                                    value: TEXT_LONG_MAX_LENGTH,
                                    message: t('maxCharactersExceeded', {
                                        max: TEXT_LONG_MAX_LENGTH
                                    })
                                }
                            });
                            return {
                                ...reg,
                                onBlur: async (e) => {
                                    await reg.onBlur(e);
                                    saveNow();
                                },
                                onChange: async (e) => {
                                    await reg.onChange(e);
                                    isSavedRef.current = false;
                                    saveDebounced(e.target.value);
                                }
                            };
                        })()}
                    />
                    <FormHelperText fontSize="xs">
                        {t('characterCount', {
                            current: (watch(fieldName) as unknown as string)?.length ?? 0,
                            max: TEXT_LONG_MAX_LENGTH
                        })}
                    </FormHelperText>
                </>
            )}
            {question.responseType === 'choices_dropdown' && (
                <Controller
                    name={fieldName}
                    control={control}
                    rules={{
                        required: question.required ? t('fieldRequired') : false
                    }}
                    render={({ field }) => (
                        <Select
                            ref={field.ref}
                            placeholder={question.responsePlaceholder ?? undefined}
                            value={(field.value as unknown as string) ?? ''}
                            name={fieldName}
                            onBlur={field.onBlur}
                            onChange={(e) => {
                                field.onChange(e.target.value);
                                isSavedRef.current = false;
                                saveNow();
                            }}
                            maxWidth="400px"
                        >
                            {question.choices.map((choice) => (
                                <option key={choice.id} value={choice.choiceText}>
                                    {choice.choiceText}
                                </option>
                            ))}
                        </Select>
                    )}
                />
            )}
            {question.responseType === 'choices_radio' && (
                <Controller
                    name={fieldName}
                    control={control}
                    rules={{
                        required: question.required ? t('fieldRequired') : false
                    }}
                    render={({ field }) => (
                        <RadioGroup
                            value={(field.value as unknown as string) ?? ''}
                            onChange={(val) => {
                                field.onChange(val);
                                isSavedRef.current = false;
                                saveNow();
                            }}
                            aria-label={question.questionText}
                        >
                            <Stack direction="column" spacing={2}>
                                {question.choices.map((choice) => (
                                    <Radio
                                        key={choice.id}
                                        value={choice.choiceText}
                                        name={fieldName}
                                        onBlur={field.onBlur}
                                    >
                                        {choice.choiceText}
                                    </Radio>
                                ))}
                            </Stack>
                        </RadioGroup>
                    )}
                />
            )}
            {question.responseType === 'choices_checkboxes' && (
                <Controller
                    name={fieldName}
                    control={control}
                    rules={{
                        validate: (value) =>
                            !question.required ||
                            (Array.isArray(value) && value.length > 0) ||
                            t('atLeastOneOptionRequired')
                    }}
                    render={({ field }) => (
                        <CheckboxGroup
                            value={(field.value as unknown as string[]) ?? []}
                            onChange={(values) => {
                                field.onChange(values);
                                isSavedRef.current = false;
                                saveNow();
                            }}
                            aria-label={question.questionText}
                        >
                            <Stack direction="column" spacing={2}>
                                {question.choices.map((choice) => (
                                    <Checkbox
                                        key={choice.id}
                                        value={choice.choiceText}
                                        name={fieldName}
                                        onBlur={field.onBlur}
                                    >
                                        {choice.choiceText}
                                    </Checkbox>
                                ))}
                            </Stack>
                        </CheckboxGroup>
                    )}
                />
            )}
            <FormErrorMessage>{fieldError?.message}</FormErrorMessage>
        </FormControl>
    );
};

export default QuestionnaireQuestionField;
